import * as _ from 'lodash-es';
import { map } from 'rxjs/operators';
import { DRResponse, ResponseStatus } from '@app/shared/models/response';
import { BaseModel, HasId } from '@app/shared/models/base';
import { BaseList } from '@app/shared/models/list';
import { HttpServiceBundle} from '@app/core/services/base/http-service-bundle.service';
import { BaseService } from '@app/core/services/base/base.service';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { BackendUriUnits } from '@app/core/services/base/backend-uri-units';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';


// OP DATA ************************************************************************************************

export enum OP {
    copy = 'copy',
    replace = 'replace',
    remove = 'remove'
}

export interface OpData {
    op: OP;
    path: string;
    value?: any;
    from?: string;
}

export interface DrQueryParams {
    urlSuffix?: string;
    queryParams?: {};
}

export class PropOverride {
    public static NO_PROP = 'NO_PROP';
}

export abstract class BaseHttpWithBundleService extends BaseService {

    protected http: HttpClient;
    protected dialog: MatDialog;
    protected uri: string;

    protected constructor(protected httpServiceBundle: HttpServiceBundle, protected nonSmp?: boolean) {
        super();
        this.http = this.httpServiceBundle.httpClient;
        this.dialog = this.httpServiceBundle.dialog;

        this.uri = `${environment.apiUrl}/${BackendUriUnits.API}${nonSmp ? '' : '/' + BackendUriUnits.SMP}`;
    }

    protected drQuery<X extends HasId & BaseModel>(constructorX: (new (json?: any) => X) | ((json: any) => (new (json?: any) => X)), uri: string, queryParams: any, propOverride?: string): Observable<BaseList<X>> {
        return this.http.get<DRResponse<BaseList<X>>>(uri, { params: queryParams })
                        .pipe(map(res => {
            return new BaseList(constructorX, this.propOverride(res, propOverride))
        }));
    }

    protected drCreate<X extends HasId & BaseModel>(constructorX: (new (json?: any) => X) | ((json: any) => (new (json?: any) => X)), uri: string, base: X, queryParams?: any, propOverride?: string): Observable<X> {
        return this.http.post<DRResponse<X>>(uri, base, { params: queryParams })
                        .pipe(map(res => {
            const constructorXX = this.getConstructor<X>(constructorX, res);
            return this.failedSuccessFormatter(res, new constructorXX(this.propOverride(res, propOverride)));
        }));
    }

    protected drUpdate<X extends HasId & BaseModel>(constructorX: (new (json?: any) => X) | ((json: any) => (new (json?: any) => X)), uri: string, base: X, queryParams?: any, propOverride?: string): Observable<X> {
        return this.http.put<DRResponse<X>>(uri, base, { params: queryParams })
                        .pipe(map(res => {
            const constructorXX = this.getConstructor<X>(constructorX, res);
            return this.failedSuccessFormatter(res, new constructorXX(this.propOverride(res, propOverride)));
        }));
    }

    protected drPatch<X extends HasId & BaseModel>(uri: string, patchData: OpData[], params?: DrQueryParams, propOverride?: string): Observable<X> {
        const queryParams = params && params.queryParams ? params.queryParams : null;
        return this.http.patch<DRResponse<X>>(uri, patchData, { params: queryParams })
                        .pipe(map(res => (this.failedSuccessFormatter(res, this.propOverride(res, propOverride)))));
    }

    protected drPatchAndUpdate<X extends HasId & BaseModel>(uri: string, base: X, patchData: OpData[], params?: DrQueryParams, disregardIds?: boolean): Observable<X> {
        return this.drPatch(uri, patchData, params)
                   .pipe(map(res => {
            if (res && base && (!base.id || res.id === base.id || disregardIds)) {
                const updated = _.mergeWith(base, res, (a, b) =>
                    _.isArray(b) ? b : undefined
                );

                for (let i = 0; i < patchData.length; i++) {
                    const patch = patchData[i];

                    if (patch.op === OP.remove ||
                       (patch.op === OP.replace && (
                            (patch.value === null || patch.value === undefined) ||
                             patch.value instanceof Array))) {
                        const objectPath = this.convertToObjectPath(patch.path);
                        const updatedValue = _.get(res, objectPath, null);
                        _.set(updated, objectPath, updatedValue);
                    }
                }

                return updated;
            } else {
                this.handleError(new Error('ID of object is different than the ID of the response during patchAndUpdateBase operation'));
            }
        }));
    }

    protected drGetById<X extends HasId & BaseModel>(constructorX: (new (json?: any) => X) | ((json: any) => (new (json?: any) => X)), uri: string, queryParams?: any, propOverride?: string): Observable<X> {
        return this.http.get<DRResponse<X>>(uri, { params: queryParams })
                        .pipe(map(res => {
            const constructorXX = this.getConstructor<X>(constructorX, res);
            return this.failedSuccessFormatter(res, new constructorXX(this.propOverride(res, propOverride)));
        }));
    }

    protected drDelete(uri: string): Observable<boolean> {
        return this.http.delete<DRResponse<boolean>>(uri)
                        .pipe(map(res => res && res.data));
    }

    protected convertToObjectPath(patchPath: string): string {
        if (patchPath) {
            // Replace all / by .
            return patchPath.replace(/\//g, '.');
        }
        return '';
    }

    private propOverride(result: any, propOverride: string) {
        return propOverride ? (propOverride === PropOverride.NO_PROP ? result : result[propOverride]) : result.data;
    }

    protected failedSuccessFormatter<X extends BaseModel>(response: DRResponse<any>, base: X): X {
        if (response.status === ResponseStatus.FAIL && base) {
            base.be_smp_warning = {
                status: response.status,
                message: response.message,
                additional_code: response.additional_code,
                additional_data: response.additional_data,
            };
        }

        return base;
    }

    public getConstructor<X extends HasId & BaseModel>(constructorOrConstructorFactory: (new (json?: any) => X) | ((json: any) => (new (json?: any) => X)), json?: any): new (json?: any) => X {
        try {
            // Test if we can call the param as a constructor
            // If no errors we return it as it is the constructor we are looking for

            // @ts-ignore
            const test = new constructorOrConstructorFactory();
            return constructorOrConstructorFactory as new (json?: any) => X;
        } catch (err) {
            // If an error occurs then we are dealing with the constructor factory
            // and we call the factory method with the provided json to determine the correct subclassed constructor.

            // @ts-ignore
            return constructorOrConstructorFactory(json) as new (json?: any) => X;
        }
    }
}
